import { Button, Dialog, FieldLabelText, Message } from 'Components/Core';
import { Table } from 'Components/Layout';
import { getConflictData, getWorksheetTitle } from 'Data/Helpers/worksheetHelpers';
import { actions } from 'Data/ReduxViewModels/WorksheetBatchUpdate';
import deepEquals from 'Lib/Object/deepEquals';
import moment from 'moment';
import React from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import WorksheetBatchUpdateSelectionGridInjectedPropsProvider from '../WorksheetBatchUpdateSelectionGrid/WorksheetBatchUpdateSelectionGridInjectedPropsProvider';
import './WorksheetBatchUpdateConflictResolutionDialog.scss';

/**
 * Component containing content for resolution dialog
 * In a separate component so it only renders when visible
 */
const WorksheetBatchUpdateConflictResolutionDialogContents: React.FC = () => {
  const worksheetConflictData = useSelector(
    (state) =>
      Object.fromEntries(
        Object.entries(state.worksheetBatchUpdate.worksheets)
          .filter(([_, v]) => v.syncModel_synced.hasSyncConflict)
          .map(([k, v]) => {
            return [
              k,
              {
                ...getConflictData(v),
                title: getWorksheetTitle(v.syncModel),
              },
            ];
          })
      ),
    deepEquals
  );

  return (
    <div className="WorksheetBatchUpdateConflictResolutionDialog-Content">
      <div className="WorksheetBatchUpdateConflictResolutionDialog-Warning">
        <Message warning>
          <FaExclamationTriangle size="24" style={{ marginBottom: '-4px' }} />
          {' The batch update could not be fully applied due to the following conflicting changes. '}
        </Message>
      </div>

      <div className="WorksheetBatchUpdateConflictResolutionDialog-Changes">
        {Object.entries(worksheetConflictData).map(([k, w]) => (
          <div key={k}>
            <Table
              colWidths={600}
              colHeaders={[
                <>
                  <span>
                    {w.title} <br /> Changed by:{' '}
                  </span>
                  {w.syncConflictUsers?.map((user, i) => (
                    <span key={i}>
                      {i > 0 ? ', ' : ''}
                      <strong>{user.Name}</strong>
                      <time dateTime={user.Timestamp}>
                        <em>{user.Timestamp && ` (${moment(user.Timestamp).fromNow()})`}</em>
                      </time>
                    </span>
                  ))}
                </>,
              ]}
            />
            {/*
          Use the selection grid props provider to inject properties for this worksheet (used for field labelling)
          */}
            <WorksheetBatchUpdateSelectionGridInjectedPropsProvider worksheetId={k}>
              <Table
                colHeaders={['Field', 'Your Value', 'Their Value']}
                colWidths={[300, 150, 150]}
                data={Object.entries(w.syncConflictFields).map(([k, v]) => [
                  <FieldLabelText name={k} />,
                  v.clientValue,
                  v.serverValue,
                ])}
                alternateRowShading={true}
              />
            </WorksheetBatchUpdateSelectionGridInjectedPropsProvider>
          </div>
        ))}
      </div>

      <div className="WorksheetBatchUpdateConflictResolutionDialog-Prompt">
        <span>
          Do you want to retry the batch update? This will reapply the update data to the latest worksheet values.
        </span>
      </div>
    </div>
  );
};

/**
 * Component that will show a conflict resolution dialog for the batch update page
 */
const WorksheetBatchUpdateConflictResolutionDialog: React.FC = () => {
  const resolvingConflict = useSelector((s) => s.worksheetBatchUpdate.status.resolvingUpdateConflict);
  const dispatch = useDispatch();

  const alwaysShow = false;
  return (
    <Dialog
      title="Conflict Encountered"
      alwaysEnabled
      visible={resolvingConflict || alwaysShow}
      width="600px"
      buttons={[
        <Button key="1" onClick={() => dispatch(actions.applyUpdateConflictResolve({ choice: 'client' }))}>
          Retry
        </Button>,
        <Button key="2" onClick={() => dispatch(actions.applyUpdateConflictResolve({ choice: 'server' }))}>
          Cancel
        </Button>,
      ]}
    >
      {(resolvingConflict || alwaysShow) && <WorksheetBatchUpdateConflictResolutionDialogContents />}
    </Dialog>
  );
};

export default WorksheetBatchUpdateConflictResolutionDialog;
export { WorksheetBatchUpdateConflictResolutionDialog };
